import { ApplicationRef, Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { LoginModalComponent } from '@kimbocare/auth';
import {
  extractQueryParameters,
  InvitationResponse,
  SessionService,
  UseCaseGroupService,
  CustomNavigationService,
  NotificationService,
  ModalService,
  HealthPackageDataService,
  ContactDataService,
  LoadingService,
  SyncStorageService,
  RestAuthentificationService,
  IUser,
} from '@kimbocare/core';
import {
  combineLatest,
  filter,
  first,
  fromEvent,
  map,
  merge,
  of,
  Subscription,
} from 'rxjs';

import { TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { LocalSessionService } from './core/services/local-session.service';
import { WhiteLabel } from './core/models/white-label';
import { ThemeService } from './core/services/theme.service';
import { EntityDataModule, EntityDataService } from '@ngrx/data';
@Component({
  selector: 'app-root',
  imports: [CommonModule, RouterOutlet, EntityDataModule],
  providers: [HealthPackageDataService, ContactDataService],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  isGuest = false;
  isLoggedIn = false;
  currentLanguage = '';
  networkStatus = false;
  $networkStatus: Subscription = Subscription.EMPTY;
  whiteLabel!: WhiteLabel;
  constructor(
    eds: EntityDataService,
    healthPackageDataService: HealthPackageDataService,
    contactDataService: ContactDataService,
    private translateService: TranslateService,
    private appRef: ApplicationRef,
    private router: Router,
    private sessionService: SessionService,
    private navigationService: CustomNavigationService,
    private useCaseGroup: UseCaseGroupService,
    private loadingService: LoadingService,
    private modalService: ModalService,
    private notificationService: NotificationService,
    private snackbar: MatSnackBar,
    private localSessionService: LocalSessionService,
    private themeService: ThemeService,
  ) {
    eds.registerServices({
      HealthPackage: healthPackageDataService,
      Contact: contactDataService,
    });
    translateService.addLangs(['en', 'fr']);
    translateService.setDefaultLang('en');
    const previousChoseLanguage = this.sessionService.getLanguage();
    const browserLang = previousChoseLanguage
      ? previousChoseLanguage
      : translateService.getBrowserLang();
    if (browserLang)
      translateService.use(browserLang.match(/en|fr/) ? browserLang : 'en');

    this.sessionService
      .isGuest()
      .subscribe((isGuest) => (this.isGuest = isGuest));

    this.whiteLabel = this.localSessionService.getWhiteLabel();
    this.sessionService.isLoggedIn().subscribe((isLoggedIn) => {
      this.isLoggedIn = isLoggedIn;
    });
    this.themeService.init().subscribe();
  }

  ngOnDestroy(): void {
    this.$networkStatus?.unsubscribe();
  }

  ngOnInit(): void {
    this.checkNetworkStatus();
    //Because query external parameters from angular app when app is starting  if too busy 😅
    const queryParams = extractQueryParameters(window.location.href, [
      'mode',
      'key',
      'identification',
      'invitation_key',
      'id',
    ]);

    combineLatest([
      this.appRef.isStable.pipe(first((isStable) => isStable)),
      this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
        first(),
      ),
    ]).subscribe(() => {
      if (queryParams) {
        if (!this.isLoggedIn || this.isGuest) {
          if (
            queryParams['mode'] ||
            queryParams['key'] ||
            queryParams['invitation_key'] ||
            queryParams['id']
          ) {
            this.navigationService.goToWithParams('login', queryParams);
          }
        } else if (this.isLoggedIn && !this.isGuest) {
          if (queryParams['invitation_key']) {
            this.addPendindInvitation(queryParams['invitation_key']);
          } else {
            this.inviteMember(queryParams);
          }
        }
      }
      console.log('App fully loaded and stable!');
    });
  }

  checkNetworkStatus() {
    this.networkStatus = navigator.onLine;
    this.$networkStatus = merge(
      of(null),
      fromEvent(window, 'online'),
      fromEvent(window, 'offline'),
    )
      .pipe(map(() => navigator.onLine))
      .subscribe((status) => {
        this.networkStatus = status;
        if (!this.networkStatus) {
          this.snackbar
            .open(
              this.currentLanguage === 'en'
                ? 'You are offline!'
                : 'Vous êtes hors ligne!',
              this.currentLanguage === 'en' ? 'Refresh' : 'Rafraîchir',
              {
                duration: 7000,
              },
            )
            .onAction()
            .subscribe(() => {
              window.location.reload();
            });
        }
      });
  }

  inviteMember(queryParams: any): void {
    this.loadingService.show();
    this.useCaseGroup.verifyAndAddMember(queryParams['key']).subscribe({
      next: (res: InvitationResponse) => {
        setTimeout(() => {
          if (res.status === 400) {
            this.notificationService.warning(
              this.translateService.instant(
                'common.wrong-invitation-account-message',
                {
                  identifier: queryParams['identification'],
                },
              ),
            );
            this.modalService
              .openModal(LoginModalComponent, {
                width: '950px',
                height: 'auto',
                data: {
                  caller: '_SIGN_IN',
                  invitation: {
                    key: queryParams['key'],
                    mode: queryParams['mode'],
                    identifier: queryParams['identification'],
                  },
                },
              })
              .subscribe();
          } else if (res.status === 200) {
            this.sessionService.storeCurrentGroup(res.group);
            this.notificationService.success(
              this.translateService.instant('common.invitation-success'),
            );
          } else if (res.status === 404) {
            this.notificationService.warning(
              this.translateService.instant(
                'common.invitation-not-exist-label',
              ),
            );
          }
        }, 1000);
      },
      error: () => {
        this.notificationService.danger(
          this.translateService.instant('errors.generic'),
        );
        this.loadingService.hide();
      },
      complete: () => {
        this.loadingService.hide();
      },
    });
  }

  addPendindInvitation(uuid: string): void {
    this.loadingService.show();
    this.useCaseGroup.addMember(uuid).subscribe({
      next: () => {
        this.notificationService.success(
          this.translateService.instant('common.add-pending-member'),
        );
      },
      error: (err) => {
        this.notificationService.danger(
          this.translateService.instant(err.translate ?? 'errors.generic'),
        );
        this.loadingService.hide();
      },
      complete: () => {
        this.loadingService.hide();
      },
    });
  }

  refreshApp(): void {
    window.location.reload();
  }
}
